import { zodResolver } from "@hookform/resolvers/zod"
import { createFileRoute } from "@tanstack/react-router"
import { Archive, Building2, Lock, Trash2 } from "lucide-react"
import { useEffect, useMemo } from "react"
import { type Resolver, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import type { Tenant } from "@/api/generated/Tenant"
import type { TenantSettings } from "@/api/generated/TenantSettings"
import { useAgents } from "@/api/queries/agents"
import { useModels, useProviders } from "@/api/queries/providers"
import { useSession } from "@/api/queries/session"
import { type TenantSettingsUpdate, useTenantSettings, useUpdateTenantSettings } from "@/api/queries/settings"
import { useTenant, useUpdateTenant } from "@/api/queries/tenants"
import {
  ComboboxField,
  NumberField,
  type Option,
  SelectField,
  SwitchField,
  TagInputField,
  TextField,
} from "@/components/app/fields"
import { FormRoot, FormRootError, SubmitButton, UnsavedChangesBar } from "@/components/app/form"
import { PageContainer, PageHeader, PageSection, Panel } from "@/components/app/page"
import { RelativeTime } from "@/components/app/relative-time"
import { ErrorState, FormSkeleton } from "@/components/app/states"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { tenantPermissions, useTenantActions } from "@/features/tenants/tenant-actions"
import i18n from "@/i18n"
import { requireCap } from "@/lib/route-guards"
import { cn } from "@/lib/utils"
import { z } from "@/lib/zod"

export const Route = createFileRoute("/_app/settings/organization")({
  beforeLoad: requireCap("management"),
  staticData: { titleKey: "nav.items.general" },
  component: OrganizationPage,
})

const SECTIONS = [
  "general",
  "conversation",
  "knowledge",
  "files",
  "externalChat",
  "evals",
  "observability",
  "danger",
] as const

function OrganizationPage() {
  const { t } = useTranslation()
  const session = useSession()
  const tenantId = session.active_tenant?.id
  const tenant = useTenant(tenantId)
  const settings = useTenantSettings()

  return (
    <PageContainer width="wide">
      <PageHeader
        title={t("orgSettings.title")}
        description={t("orgSettings.description", { name: session.active_tenant?.name ?? "" })}
        icon={Building2}
      />
      <div className="grid gap-10 xl:grid-cols-[minmax(0,1fr)_12rem]">
        <div className="min-w-0 space-y-10">
          <PageSection
            id="general"
            layout="split"
            title={t("orgSettings.sections.general.title")}
            description={t("orgSettings.sections.general.description")}
          >
            {tenant.isPending ? (
              <Panel>
                <FormSkeleton fields={3} />
              </Panel>
            ) : tenant.isError ? (
              <ErrorState error={tenant.error} onRetry={() => void tenant.refetch()} />
            ) : (
              <TenantForm tenant={tenant.data} isPlatformAdmin={!!session.user?.is_platform_admin} />
            )}
          </PageSection>

          {settings.isPending ? (
            <Panel>
              <FormSkeleton fields={6} />
            </Panel>
          ) : settings.isError ? (
            <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
          ) : (
            <SettingsForm settings={settings.data} />
          )}

          {tenant.data && (
            <DangerZone tenant={tenant.data} isPlatformAdmin={!!session.user?.is_platform_admin} />
          )}
        </div>
        <OnThisPage />
      </div>
    </PageContainer>
  )
}

function OnThisPage() {
  const { t } = useTranslation()
  return (
    <nav aria-label={t("orgSettings.onThisPage")} className="hidden xl:block">
      <div className="sticky top-6 space-y-1 text-sm">
        <div className="text-muted-foreground mb-2 text-xs font-medium">{t("orgSettings.onThisPage")}</div>
        {SECTIONS.map((id) => (
          <a
            key={id}
            href={`#${id}`}
            className={cn(
              "text-muted-foreground hover:text-foreground block truncate border-l py-1 pl-3 transition-colors",
              id === "danger" && "hover:text-destructive",
            )}
          >
            {t(`orgSettings.sections.${id}.title`)}
          </a>
        ))}
      </div>
    </nav>
  )
}

const tenantSchema = z.object({
  name: z.string().trim().min(1).max(255),
  slug: z
    .string()
    .trim()
    .min(1)
    .max(63)
    .regex(/^[a-z0-9-]+$/, { error: () => i18n.t("tenants.form.slugInvalid") }),
  kind: z.enum(["organization", "personal"]),
})
type TenantValues = z.infer<typeof tenantSchema>

function TenantForm({ tenant, isPlatformAdmin }: { tenant: Tenant; isPlatformAdmin: boolean }) {
  const { t } = useTranslation()
  const update = useUpdateTenant(tenant.id)
  const { canEdit, isDefault } = tenantPermissions(tenant, isPlatformAdmin)
  const values = useMemo(
    () => ({
      name: tenant.name,
      slug: tenant.slug,
      kind: (tenant.kind === "personal" ? "personal" : "organization") as TenantValues["kind"],
    }),
    [tenant],
  )
  const form = useForm<TenantValues>({
    resolver: zodResolver(tenantSchema),
    defaultValues: values,
    disabled: !canEdit,
  })
  useEffect(() => form.reset(values), [values, form])

  return (
    <FormRoot form={form} onSubmit={async (v) => void (await update.mutateAsync(v))}>
      <Panel
        footer={
          canEdit ? (
            <SubmitButton form={form} disabledWhenPristine size="sm">
              {t("common.save")}
            </SubmitButton>
          ) : undefined
        }
      >
        <div className="grid gap-4">
          {!canEdit && (
            <Alert>
              <Lock />
              <AlertDescription>{t("orgSettings.readOnly")}</AlertDescription>
            </Alert>
          )}
          <TextField control={form.control} name="name" label={t("tenants.form.name")} required />
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="slug"
              label={t("tenants.form.slug")}
              description={isDefault ? t("orgSettings.defaultSlug") : t("tenants.form.slugEditHint")}
              mono
              readOnly={isDefault}
            />
            <SelectField
              control={form.control}
              name="kind"
              label={t("tenants.form.kind")}
              options={[
                { value: "organization", label: t("tenants.kind.organization") },
                { value: "personal", label: t("tenants.kind.personal") },
              ]}
            />
          </div>
          <FormRootError form={form} />
        </div>
      </Panel>
    </FormRoot>
  )
}

// ---------------------------------------------------------------------------- tenant settings

const int = (min: number, max: number) => z.number().int().min(min).max(max)
const optionalId = z.number().int().positive().nullable()

/** Keeps a saved id selectable (shown as `#id`) while its list loads or after it was removed. */
function withCurrent(options: Option<number>[], current: number | null | undefined): Option<number>[] {
  return current == null || options.some((o) => o.value === current)
    ? options
    : [...options, { value: current, label: `#${current}` }]
}

const settingsSchema = z.object({
  timezone: z.string().min(1),
  summary_model_id: optionalId,
  summary_trigger_turns: int(1, 1000),
  max_tool_response_chars: int(100, 1_000_000),
  history_max_messages: int(1, 10_000),
  history_recent_turns_full: int(0, 100),
  session_facts_max_chars: int(0, 100_000),
  session_facts_update_enabled: z.boolean(),
  embedding_provider_id: optionalId,
  embedding_model: z.string().trim().max(200),
  embedding_dimensions: z.number().int().positive().max(20_000).nullable(),
  embedding_batch_size: int(1, 2048),
  chat_upload_max_mb: int(1, 10_000),
  project_file_max_size_mb: int(1, 10_000),
  project_files_quota_mb: int(1, 1_000_000),
  project_file_allowed_extensions: z.array(z.string()),
  connector_file_retention_days: int(0, 3650),
  connector_file_max_size_mb: int(1, 10_000),
  connector_files_quota_mb: int(1, 1_000_000),
  connector_file_allowed_extensions: z.array(z.string()),
  external_chat_enabled: z.boolean(),
  external_chat_agent_id: optionalId,
  monitor_model_id: optionalId,
  eval_judge_model_id: optionalId,
  eval_parallelism: int(1, 64),
  eval_regression_threshold_pct: z.number().min(0).max(100),
  sentry_organization_slug: z.string().trim().max(100),
  observability_url: z.union([z.literal(""), z.url()]),
})
type SettingsValues = z.infer<typeof settingsSchema>

const splitExt = (s: string) =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean)

function toValues(s: TenantSettings): SettingsValues {
  const { tenant_id: _id, updated_at: _u, ...rest } = s
  return {
    ...rest,
    project_file_allowed_extensions: splitExt(s.project_file_allowed_extensions),
    connector_file_allowed_extensions: splitExt(s.connector_file_allowed_extensions),
  }
}

/** Only send what changed (PATCH semantics). */
function toPatch(
  values: SettingsValues,
  dirty: Partial<Record<keyof SettingsValues, unknown>>,
): TenantSettingsUpdate {
  const patch: Record<string, unknown> = {}
  for (const key of Object.keys(dirty) as (keyof SettingsValues)[]) {
    if (!dirty[key]) continue
    const v = values[key]
    patch[key] = Array.isArray(v) ? v.join(",") : v
  }
  return patch as TenantSettingsUpdate
}

function timezones(): { value: string; label: string }[] {
  let zones: string[] = []
  try {
    zones = (Intl as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.("timeZone") ?? []
  } catch {
    zones = []
  }
  if (!zones.includes("UTC")) zones = ["UTC", ...zones]
  return zones.map((z) => ({ value: z, label: z.replaceAll("_", " ") }))
}

function SettingsForm({ settings }: { settings: TenantSettings }) {
  const { t } = useTranslation()
  const update = useUpdateTenantSettings()
  const values = useMemo(() => toValues(settings), [settings])
  const zones = useMemo(timezones, [])
  const form = useForm<SettingsValues>({
    resolver: zodResolver(settingsSchema) as Resolver<SettingsValues>,
    defaultValues: values,
  })
  useEffect(() => form.reset(values), [values, form])
  const c = form.control
  const models = useModels()
  const providers = useProviders()
  const agents = useAgents()
  const modelOptions = (models.data ?? []).map((m) => ({
    value: m.id,
    label: m.name,
    description: `${m.provider_name} · ${m.model}`,
  }))
  const providerOptions = (providers.data ?? []).map((p) => ({
    value: p.id,
    label: p.name,
    description: p.kind,
  }))
  const agentOptions = (agents.data ?? []).map((a) => ({
    value: a.id,
    label: a.name,
    description: a.model_name,
  }))
  const modelHint = t("orgSettings.modelPickerHint")

  return (
    <FormRoot
      form={form}
      onSubmit={async (v) => {
        const patch = toPatch(v, form.formState.dirtyFields as Record<string, unknown>)
        if (Object.keys(patch).length) await update.mutateAsync(patch)
      }}
      className="space-y-10"
    >
      <PageSection
        id="conversation"
        layout="split"
        title={t("orgSettings.sections.conversation.title")}
        description={t("orgSettings.sections.conversation.description")}
      >
        <Panel>
          <div className="grid gap-4 sm:grid-cols-2">
            <ComboboxField
              control={c}
              name="timezone"
              label={t("orgSettings.fields.timezone")}
              description={t("orgSettings.fields.timezoneHint")}
              options={zones}
              className="sm:col-span-2"
            />
            <SelectField
              control={c}
              name="summary_model_id"
              nullable
              noneLabel={t("orgSettings.notSet")}
              label={t("orgSettings.fields.summaryModel")}
              description={modelHint}
              options={withCurrent(modelOptions, settings.summary_model_id)}
            />
            <NumberField
              control={c}
              name="summary_trigger_turns"
              label={t("orgSettings.fields.summaryTriggerTurns")}
              description={t("orgSettings.fields.summaryTriggerTurnsHint")}
              unit={t("units.turns")}
              min={1}
            />
            <NumberField
              control={c}
              name="history_max_messages"
              label={t("orgSettings.fields.historyMaxMessages")}
              unit={t("units.messages")}
              min={1}
            />
            <NumberField
              control={c}
              name="history_recent_turns_full"
              label={t("orgSettings.fields.historyRecentTurnsFull")}
              description={t("orgSettings.fields.historyRecentTurnsFullHint")}
              unit={t("units.turns")}
              min={0}
            />
            <NumberField
              control={c}
              name="max_tool_response_chars"
              label={t("orgSettings.fields.maxToolResponseChars")}
              unit={t("units.chars")}
              min={100}
              step={1000}
            />
            <NumberField
              control={c}
              name="session_facts_max_chars"
              label={t("orgSettings.fields.sessionFactsMaxChars")}
              unit={t("units.chars")}
              min={0}
            />
            <SwitchField
              control={c}
              name="session_facts_update_enabled"
              variant="card"
              label={t("orgSettings.fields.sessionFactsUpdate")}
              description={t("orgSettings.fields.sessionFactsUpdateHint")}
              className="sm:col-span-2"
            />
          </div>
        </Panel>
      </PageSection>

      <PageSection
        id="knowledge"
        layout="split"
        title={t("orgSettings.sections.knowledge.title")}
        description={t("orgSettings.sections.knowledge.description")}
      >
        <Panel>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              control={c}
              name="embedding_provider_id"
              nullable
              noneLabel={t("orgSettings.notSet")}
              label={t("orgSettings.fields.embeddingProvider")}
              description={t("orgSettings.providerPickerHint")}
              options={withCurrent(providerOptions, settings.embedding_provider_id)}
            />
            <TextField
              control={c}
              name="embedding_model"
              label={t("orgSettings.fields.embeddingModel")}
              mono
              placeholder="text-embedding-3-small"
            />
            <NumberField
              control={c}
              name="embedding_dimensions"
              nullable
              label={t("orgSettings.fields.embeddingDimensions")}
              description={t("orgSettings.fields.embeddingDimensionsHint")}
              placeholder={t("orgSettings.auto")}
            />
            <NumberField
              control={c}
              name="embedding_batch_size"
              label={t("orgSettings.fields.embeddingBatchSize")}
              unit={t("units.docs")}
              min={1}
            />
          </div>
        </Panel>
      </PageSection>

      <PageSection
        id="files"
        layout="split"
        title={t("orgSettings.sections.files.title")}
        description={t("orgSettings.sections.files.description")}
      >
        <div className="space-y-4">
          <Panel>
            <h3 className="mb-4 text-[0.8125rem] font-semibold">{t("orgSettings.files.chatAndProjects")}</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <NumberField
                control={c}
                name="chat_upload_max_mb"
                label={t("orgSettings.fields.chatUploadMax")}
                unit="MB"
                min={1}
              />
              <NumberField
                control={c}
                name="project_file_max_size_mb"
                label={t("orgSettings.fields.projectFileMax")}
                unit="MB"
                min={1}
              />
              <NumberField
                control={c}
                name="project_files_quota_mb"
                label={t("orgSettings.fields.projectQuota")}
                unit="MB"
                min={1}
              />
              <TagInputField
                control={c}
                name="project_file_allowed_extensions"
                label={t("orgSettings.fields.projectExtensions")}
                description={t("orgSettings.fields.extensionsHint")}
                placeholder="md, pdf, csv…"
                validate={(tag) => /^[a-z0-9]+$/.test(tag)}
                className="sm:col-span-3"
              />
            </div>
          </Panel>
          <Panel>
            <h3 className="mb-4 text-[0.8125rem] font-semibold">{t("orgSettings.files.connectors")}</h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <NumberField
                control={c}
                name="connector_file_max_size_mb"
                label={t("orgSettings.fields.connectorFileMax")}
                unit="MB"
                min={1}
              />
              <NumberField
                control={c}
                name="connector_files_quota_mb"
                label={t("orgSettings.fields.connectorQuota")}
                unit="MB"
                min={1}
              />
              <NumberField
                control={c}
                name="connector_file_retention_days"
                label={t("orgSettings.fields.connectorRetention")}
                description={t("orgSettings.fields.connectorRetentionHint")}
                unit={t("units.days")}
                min={0}
              />
              <TagInputField
                control={c}
                name="connector_file_allowed_extensions"
                label={t("orgSettings.fields.connectorExtensions")}
                description={t("orgSettings.fields.extensionsHint")}
                placeholder="md, pdf, csv…"
                validate={(tag) => /^[a-z0-9]+$/.test(tag)}
                className="sm:col-span-3"
              />
            </div>
          </Panel>
        </div>
      </PageSection>

      <PageSection
        id="externalChat"
        layout="split"
        title={t("orgSettings.sections.externalChat.title")}
        description={t("orgSettings.sections.externalChat.description")}
      >
        <Panel>
          <div className="grid gap-4">
            <SwitchField
              control={c}
              name="external_chat_enabled"
              variant="card"
              label={t("orgSettings.fields.externalChatEnabled")}
              description={t("orgSettings.fields.externalChatEnabledHint")}
            />
            <SelectField
              control={c}
              name="external_chat_agent_id"
              nullable
              noneLabel={t("orgSettings.notSet")}
              label={t("orgSettings.fields.externalChatAgent")}
              description={t("orgSettings.agentPickerHint")}
              options={withCurrent(agentOptions, settings.external_chat_agent_id)}
            />
          </div>
        </Panel>
      </PageSection>

      <PageSection
        id="evals"
        layout="split"
        title={t("orgSettings.sections.evals.title")}
        description={t("orgSettings.sections.evals.description")}
      >
        <Panel>
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              control={c}
              name="eval_judge_model_id"
              nullable
              noneLabel={t("orgSettings.notSet")}
              label={t("orgSettings.fields.evalJudgeModel")}
              description={modelHint}
              options={withCurrent(modelOptions, settings.eval_judge_model_id)}
            />
            <SelectField
              control={c}
              name="monitor_model_id"
              nullable
              noneLabel={t("orgSettings.notSet")}
              label={t("orgSettings.fields.monitorModel")}
              description={modelHint}
              options={withCurrent(modelOptions, settings.monitor_model_id)}
            />
            <NumberField
              control={c}
              name="eval_parallelism"
              label={t("orgSettings.fields.evalParallelism")}
              description={t("orgSettings.fields.evalParallelismHint")}
              min={1}
              max={64}
            />
            <NumberField
              control={c}
              name="eval_regression_threshold_pct"
              label={t("orgSettings.fields.evalRegressionThreshold")}
              description={t("orgSettings.fields.evalRegressionThresholdHint")}
              unit="%"
              min={0}
              max={100}
              step={0.5}
            />
          </div>
        </Panel>
      </PageSection>

      <PageSection
        id="observability"
        layout="split"
        title={t("orgSettings.sections.observability.title")}
        description={t("orgSettings.sections.observability.description")}
      >
        <Panel>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={c}
              name="observability_url"
              type="url"
              label={t("orgSettings.fields.observabilityUrl")}
              description={t("orgSettings.fields.observabilityUrlHint")}
              placeholder="https://logfire.pydantic.dev/…"
              className="sm:col-span-2"
            />
            <TextField
              control={c}
              name="sentry_organization_slug"
              label={t("orgSettings.fields.sentrySlug")}
              mono
              placeholder="my-org"
            />
          </div>
          <p className="text-muted-foreground mt-4 text-xs">
            {t("orgSettings.lastUpdated")} <RelativeTime value={settings.updated_at} />
          </p>
        </Panel>
      </PageSection>

      <FormRootError form={form} />
      <UnsavedChangesBar form={form} onDiscard={() => form.reset(values)} />
    </FormRoot>
  )
}

function DangerZone({ tenant, isPlatformAdmin }: { tenant: Tenant; isPlatformAdmin: boolean }) {
  const { t } = useTranslation()
  const { canArchive, canDelete, isDefault } = tenantPermissions(tenant, isPlatformAdmin)
  const actions = useTenantActions()
  return (
    <PageSection
      id="danger"
      layout="split"
      title={<span className="text-destructive">{t("orgSettings.sections.danger.title")}</span>}
      description={t("orgSettings.sections.danger.description")}
    >
      <div className="border-destructive/30 divide-destructive/20 divide-y overflow-hidden rounded-xl border">
        <DangerRow
          title={t("orgSettings.danger.archiveTitle")}
          description={
            isDefault ? t("orgSettings.danger.defaultProtected") : t("orgSettings.danger.archiveDescription")
          }
          action={
            <Button
              variant="outline"
              size="sm"
              disabled={!canArchive}
              onClick={() => void actions.archive(tenant)}
            >
              <Archive />
              {t("orgSettings.danger.archive")}
            </Button>
          }
        />
        <DangerRow
          title={t("orgSettings.danger.deleteTitle")}
          description={
            isDefault ? t("orgSettings.danger.defaultProtected") : t("orgSettings.danger.deleteDescription")
          }
          action={
            <Button
              variant="destructive"
              size="sm"
              disabled={!canDelete}
              onClick={() => void actions.remove(tenant)}
              data-testid="delete-organization"
            >
              <Trash2 />
              {t("orgSettings.danger.delete")}
            </Button>
          }
        />
      </div>
    </PageSection>
  )
}

function DangerRow({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action: React.ReactNode
}) {
  return (
    <div className="bg-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
      <div className="space-y-0.5">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-muted-foreground text-sm text-pretty">{description}</div>
      </div>
      <div className="shrink-0">{action}</div>
    </div>
  )
}
